'use client';

import { event_view_promotion } from '.';

// The trigger is per creative ("at least 50% visible for 2 seconds") but the
// payload is an `items` array, so qualified promotions are collected and pushed
// as ONE `view_promotion` with each creative as its own entry.
//
// Spec §5.3.8: hold them "until 10 promotions are pending OR the user navigates
// away". Those two flush points alone never fire for a shopper who stays on a
// page showing fewer than 10 creatives, which is the homepage above the fold —
// DAAM v3 saw no event at all. So the buffer also flushes once it has been
// quiet for `IDLE_MS`.
//
// Creatives on screen together qualify within a frame of each other, so they
// still land in one push. The slides of an autoplaying carousel qualify seconds
// apart and each reports in its own push, which is correct — they were never
// visible as a set.
const MAX_PENDING = 10;
const IDLE_MS = 1500;

let pending = [];
let idleTimer = null;

// The trailing position is padded so `_10` sorts after `_2`.
const slotKey = (promotion) =>
  String(promotion?.creative_slot || '').replace(/\d+$/, (position) =>
    position.padStart(4, '0')
  );

export const queuePromotion = (promotion) => {
  if (!promotion?.promotion_id) return;

  pending.push(promotion);

  if (pending.length >= MAX_PENDING) {
    flushPromotions();
    return;
  }

  // Restarted on every arrival, so a set that qualifies together is pushed
  // together.
  clearTimeout(idleTimer);
  idleTimer = setTimeout(flushPromotions, IDLE_MS);
};

export const flushPromotions = () => {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }

  const items = pending;
  pending = [];

  if (!items.length) return;

  // Report in slot order, not in the order the viewport happened to reveal.
  items.sort((a, b) => slotKey(a).localeCompare(slotKey(b)));

  event_view_promotion(items);
};

// Leaving the site is the other flush point. `visibilitychange` fires early
// enough to be reliable where `pagehide` alone is not, and flushing an empty
// buffer is a no-op, so registering both is safe.
if (typeof window === 'object') {
  window.addEventListener('pagehide', flushPromotions);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPromotions();
  });
}

// The ticker sits in the header on every route, so the per-page-view guard would
// report it again on each navigation. These creatives are counted once per
// session instead, which survives route changes but not a new tab.
const SESSION_KEY = 'gtm_session_promotions';

const readSession = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY));
    return new Set(Array.isArray(stored) ? stored : []);
  } catch {
    return new Set();
  }
};

export const wasPromotionSeenThisSession = (promotionId) =>
  readSession().has(promotionId);

export const markPromotionSeenThisSession = (promotionId) => {
  const seen = readSession();
  seen.add(promotionId);

  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...seen]));
  } catch {
    return;
  }
};
